import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  View, Text, StyleSheet, TouchableOpacity, ScrollView, SafeAreaView, StatusBar,
  Image, ActivityIndicator, Share, Linking, Alert, Animated, Dimensions,
  Modal, RefreshControl, Platform,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { apiFetch } from '../../utils/apiClient';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';
import useProfile from '../../hooks/useProfile';

let LinearGradient;
try { LinearGradient = require('expo-linear-gradient').LinearGradient; }
catch { LinearGradient = ({colors,style,children,...p})=><View style={[style,{backgroundColor:colors?.[0]||'#0e4e96'}]} {...p}>{children}</View>; }

const { width: SW } = Dimensions.get('window');

// ═══════════════════════════════════════════════════════════════════════════════
// API
// ═══════════════════════════════════════════════════════════════════════════════
const API_BASE = 'https://api.jobeng.ai';
const API_KEY  = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
const PROF_KEYS = { PROFILE:'prof_user_profile' };
const APPS_KEY  = 'job_applications_data';

const T = {
  primary:'#0e4e96', primaryLight:'#1e7be2', primaryBg:'#EFF6FF',
  success:'#10b981', successBg:'#D1FAE5',
  warning:'#f59e0b', warningBg:'#FEF3C7',
  danger:'#ef4444',  dangerBg:'#FEE2E2',
  text:'#1F2937', textSec:'#64748B', textMut:'#94A3B8',
  border:'#E2E8F0', borderLight:'#F1F5F9',
  card:'#FFF', bg:'#F8FAFC', white:'#FFF',
};

const fmtDate = d => { try { return new Date(d).toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'}); } catch { return ''; } };
const fmtSalary = (min,max,cur='GBP') => { const s=cur==='GBP'?'£':'$'; if(min&&max) return `${s}${(min/1000).toFixed(0)}k–${s}${(max/1000).toFixed(0)}k`; if(min) return `From ${s}${(min/1000).toFixed(0)}k`; if(max) return `Up to ${s}${(max/1000).toFixed(0)}k`; return null; };
const timeAgo = d => { try { const h=Math.floor((Date.now()-new Date(d))/(36e5)); if(h<1)return 'Just posted'; if(h<24)return `${h}h ago`; const dy=Math.floor(h/24); if(dy<7)return `${dy}d ago`; if(dy<30)return `${Math.floor(dy/7)}w ago`; return `${Math.floor(dy/30)}mo ago`; } catch { return ''; } };
const daysLeft = d => { try { const days=Math.ceil((new Date(d)-Date.now())/(864e5)); return days; } catch { return null; } };

const TABS = [
  {key:'overview',    label:'Overview',     icon:'document-text-outline'},
  {key:'requirements',label:'Requirements', icon:'checkmark-circle-outline'},
  {key:'benefits',    label:'Benefits',     icon:'gift-outline'},
  {key:'company',     label:'Company',      icon:'business-outline'},
  {key:'similar',     label:'Similar',      icon:'copy-outline'},
];

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════
export default function JobDetailsScreen({ navigation, route }) {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // ── Extract params ──
  const jobId = route.params?.jobId || route.params?.id || route.params?.job?.id || null;
  const initialJob = route.params?.job || route.params?.jobData || route.params?.item || route.params?.data || null;

  // ── State ──
  const [isLoading, setIsLoading]   = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [job, setJob]               = useState(null);
  const [isSaved, setIsSaved]       = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [hasApplied, setHasApplied] = useState(false);
  const [activeTab, setActiveTab]   = useState('overview');
  const [similarJobs, setSimilarJobs] = useState([]);
  const [matchScore, setMatchScore] = useState(0);
  const [userSkills, setUserSkills] = useState([]);
  const [error, setError]           = useState(null);
  const [showReportModal, setShowReportModal] = useState(false);

  const fadeAnim  = useRef(new Animated.Value(0)).current;
  const slideAnim = useRef(new Animated.Value(30)).current;

  // ═════════════════════════════════════════════════════════════════════════════
  // INIT
  // ═════════════════════════════════════════════════════════════════════════════
  useEffect(() => { loadJobDetails(); }, []);

  const loadJobDetails = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      // 1. Set job data
      let jobData = initialJob;
      if (!jobData && jobId) {
        const r = await apiFetch(`/api/data/jobs/${jobId}`);
        if (!r.error) jobData = r.data || r;
      }
      if (!jobData && jobId) {
        const r = await apiFetch(`/api/data/jobs?id=${jobId}&limit=1`);
        if (!r.error && Array.isArray(r) && r.length>0) jobData = r[0];
      }
      if (!jobData) { setError('Job not found.'); setIsLoading(false); return; }

      // Normalize fields
      jobData = {
        ...jobData,
        salary: jobData.salary || fmtSalary(jobData.salary_min,jobData.salary_max,jobData.salary_currency) || '',
        requirements: parseList(jobData.requirements || jobData.required_skills),
        responsibilities: parseList(jobData.responsibilities),
        benefits: parseList(jobData.benefits),
        tags: parseList(jobData.required_skills || jobData.tags),
      };
      setJob(jobData);

      // 2. Load user skills for match score
      const profRaw = await AsyncStorage.getItem(PROF_KEYS.PROFILE) || await AsyncStorage.getItem('userProfile');
      if (profRaw) {
        const prof = JSON.parse(profRaw);
        const skills = (prof.skills||[]).map(s=>(s.name||s.skill_name||'').toLowerCase()).filter(Boolean);
        setUserSkills(skills);
        calcMatchScore(jobData, skills);
      }

      // 3. Check if already applied
      const appsRaw = await AsyncStorage.getItem(APPS_KEY);
      if (appsRaw) {
        const apps = JSON.parse(appsRaw);
        const applied = (Array.isArray(apps)?apps:[]).some(a=>a.jobId===(jobData.id||jobId) && a.status!=='draft');
        setHasApplied(applied);
      }
      // Also check API
      const apiApps = await apiFetch(`/api/data/applications?job_id=${jobData.id||jobId}&limit=1`);
      if (!apiApps.error && Array.isArray(apiApps) && apiApps.length>0) setHasApplied(true);

      // 4. Check if saved
      const savedRaw = await AsyncStorage.getItem('prof_cached_saved_jobs');
      if (savedRaw) {
        const saved = JSON.parse(savedRaw);
        setIsSaved((Array.isArray(saved)?saved:[]).some(s=>s.job_id==(jobData.id||jobId)||s.id==(jobData.id||jobId)));
      }

      // 5. Load similar jobs
      loadSimilarJobs(jobData);

      // 6. Animate
      Animated.parallel([
        Animated.timing(fadeAnim,{toValue:1,duration:600,useNativeDriver:true}),
        Animated.timing(slideAnim,{toValue:0,duration:600,useNativeDriver:true}),
      ]).start();
    } catch(e) {
      setError('Failed to load job details.');
    }
    setIsLoading(false);
  }, [jobId, initialJob]);

  const parseList = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    if (typeof val === 'string') {
      try { const parsed = JSON.parse(val); if (Array.isArray(parsed)) return parsed; } catch {}
      return val.split('\n').map(s=>s.replace(/^[•\-*]\s*/,'').trim()).filter(Boolean);
    }
    return [];
  };

  const calcMatchScore = (jobData, skills) => {
    if (!jobData || !skills.length) { setMatchScore(0); return; }
    const jobSkills = parseList(jobData.required_skills || jobData.requirements || jobData.tags)
      .map(s=>(typeof s==='string'?s:'').toLowerCase());
    if (!jobSkills.length) { setMatchScore(0); return; }
    const matched = jobSkills.filter(js => skills.some(us => us.includes(js) || js.includes(us))).length;
    setMatchScore(Math.min(100, Math.round((matched / jobSkills.length) * 80) + (jobData.is_remote?10:0) + 10));
  };

  const loadSimilarJobs = useCallback(async (jobData) => {
    try {
      const dept = jobData?.department || '';
      const r = await apiFetch(`/api/data/jobs?is_active=true&limit=5&sort=created_at&order=desc`);
      if (!r.error && Array.isArray(r)) {
        setSimilarJobs(r.filter(j=>j.id!==(jobData?.id||jobId)).slice(0,4));
      }
    } catch {}
  }, [jobId]);

  const onRefresh = useCallback(async () => { setRefreshing(true); await loadJobDetails(); setRefreshing(false); }, [loadJobDetails]);

  // ═════════════════════════════════════════════════════════════════════════════
  // ACTIONS
  // ═════════════════════════════════════════════════════════════════════════════
  const handleApply = useCallback(async () => {
    if (hasApplied) { Alert.alert('Already Applied','You have already applied to this job.'); return; }
    setIsApplying(true);
    try {
      navigation.navigate('JobApplication', { jobId:job?.id||jobId, job });
    } catch {
      // Fallback: direct apply via shared helper (fans out to both tables)
      try {
        const profRaw = await AsyncStorage.getItem(PROF_KEYS.PROFILE);
        const prof = profRaw ? JSON.parse(profRaw) : {};
        const p = prof.personal || {};
        const { applyToJob } = require('./jobsShared');
        await applyToJob({
          job_id:job?.id||jobId, job_title:job?.title||'', company:job?.company||job?.department||'',
          candidate_name:p.fullName||'', candidate_email:p.email||'', source:'job_details_screen',
        });
        setHasApplied(true);
        Alert.alert('✅ Applied!','Your application has been submitted.');
      } catch { Alert.alert('Error','Could not submit application.'); }
    }
    setIsApplying(false);
  }, [job, jobId, hasApplied, navigation]);

  const handleSave = useCallback(async () => {
    if (!job) return;
    try {
      if (isSaved) {
        // Unsave
        const savedRaw = await AsyncStorage.getItem('prof_cached_saved_jobs');
        if (savedRaw) {
          const saved = JSON.parse(savedRaw).filter(s=>s.job_id!=(job.id||jobId)&&s.id!=(job.id||jobId));
          await AsyncStorage.setItem('prof_cached_saved_jobs', JSON.stringify(saved));
        }
        await apiFetch(`/api/data/saved_jobs/${job.id}`,{method:'DELETE'}).catch(()=>{});
        setIsSaved(false);
      } else {
        // Save
        const payload = { job_id:job.id, title:job.title, company:job.company||job.department, location:job.location, salary_min:job.salary_min, salary_max:job.salary_max };
        await apiFetch('/api/data/saved_jobs',{method:'POST',body:JSON.stringify(payload)}).catch(()=>{});
        setIsSaved(true);
      }
    } catch { Alert.alert('Error','Could not save job.'); }
  }, [job, jobId, isSaved]);

  const handleShare = useCallback(async () => {
    if (!job) return;
    try {
      await Share.share({ title:`${job.title} — ${job.company||''}`, message:`Check out this job: ${job.title} at ${job.company||job.department||''}\nLocation: ${job.location||''}\nSalary: ${job.salary||''}\n\nApply on JOBENG!` });
    } catch {}
  }, [job]);

  const handleReport = useCallback(() => {
    Alert.alert('Report Job','Why are you reporting this job?',[
      {text:'Cancel',style:'cancel'},
      {text:'Spam / Scam',onPress:()=>Alert.alert('Reported','Thank you for your report. We will review this listing.')},
      {text:'Inaccurate Info',onPress:()=>Alert.alert('Reported','Thank you. We will verify the listing details.')},
      {text:'Discriminatory',onPress:()=>Alert.alert('Reported','This has been flagged for review.')},
    ]);
  }, []);

  // ═════════════════════════════════════════════════════════════════════════════
  // COMPUTED
  // ═════════════════════════════════════════════════════════════════════════════
  const deadline = useMemo(() => daysLeft(job?.application_deadline || job?.applicationDeadline), [job]);
  const salary = job?.salary || fmtSalary(job?.salary_min,job?.salary_max,job?.salary_currency) || 'Competitive';
  const jobType = job?.job_type || job?.type || job?.jobType || 'Full-time';
  const isRemote = job?.is_remote || job?.remote || job?.isRemote || false;
  const company = job?.company || job?.department || '';
  const postedTime = timeAgo(job?.posted_date || job?.posted || job?.created_at);

  // ═════════════════════════════════════════════════════════════════════════════
  // LOADING / ERROR
  // ═════════════════════════════════════════════════════════════════════════════
  if (isLoading) return (
    <SafeAreaView style={{flex:1,backgroundColor:T.bg}}>
      <StatusBar barStyle="light-content" backgroundColor={T.primary}/>
      <View style={{flex:1,justifyContent:'center',alignItems:'center'}}>
        <ActivityIndicator size={36} color={T.primary}/>
        <Text style={{marginTop:12,color:T.textSec}}>Loading job details...</Text>
      </View>
    </SafeAreaView>
  );

  if (error || !job) return (
    <SafeAreaView style={{flex:1,backgroundColor:T.bg}}>
      <StatusBar barStyle="light-content" backgroundColor={T.primary}/>
      <View style={{flex:1,justifyContent:'center',alignItems:'center',paddingHorizontal:32}}>
        <Ionicons name={error?'alert-circle':'document-outline'} size={56} color={error?T.danger:T.textMut}/>
        <Text style={{fontSize:20,fontWeight:'700',color:T.text,marginTop:14}}>{error||'Job Not Found'}</Text>
        <TouchableOpacity style={s.retryBtn} onPress={error?loadJobDetails:()=>navigation.goBack()}>
          <Ionicons name={error?'refresh':'arrow-back'} size={18} color={T.white}/>
          <Text style={{color:T.white,fontWeight:'600',marginLeft:6}}>{error?'Retry':'Go Back'}</Text>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER: HEADER
  // ═════════════════════════════════════════════════════════════════════════════
  const renderHeader = () => (
    <LinearGradient colors={[T.primary,T.primaryLight]} style={s.header}>
      <TouchableOpacity style={s.hdrBtn} onPress={()=>navigation.goBack()}>
        <Ionicons name="arrow-back" size={22} color={T.white}/>
      </TouchableOpacity>
      <View style={{flex:1,marginHorizontal:12}}>
        <Text style={s.hdrTitle} numberOfLines={1}>Job Details</Text>
        <Text style={s.hdrSub} numberOfLines={1}>{company}</Text>
      </View>
      <TouchableOpacity style={s.hdrBtn} onPress={handleShare}>
        <Ionicons name="share-outline" size={20} color={T.white}/>
      </TouchableOpacity>
      <TouchableOpacity style={[s.hdrBtn,{marginLeft:6}]} onPress={handleReport}>
        <Ionicons name="flag-outline" size={18} color={T.white}/>
      </TouchableOpacity>
    </LinearGradient>
  );

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER: JOB HERO
  // ═════════════════════════════════════════════════════════════════════════════
  const renderHero = () => (
    <Animated.View style={[s.heroCard,{opacity:fadeAnim,transform:[{translateY:slideAnim}]}]}>
      {/* Company + Title */}
      <View style={{flexDirection:'row',marginBottom:14}}>
        <View style={s.logoCircle}>
          <Text style={s.logoText}>{(company||'?')[0].toUpperCase()}</Text>
        </View>
        <View style={{flex:1,marginLeft:12}}>
          <Text style={s.jobTitle}>{job.title}</Text>
          <Text style={s.companyName}>{company}</Text>
          <View style={{flexDirection:'row',flexWrap:'wrap',gap:6,marginTop:6}}>
            <View style={s.metaChip}><Ionicons name="location-outline" size={12} color={T.textSec}/><Text style={s.metaChipT}>{job.location||'Not specified'}</Text></View>
            <View style={s.metaChip}><Ionicons name="time-outline" size={12} color={T.textSec}/><Text style={s.metaChipT}>{postedTime}</Text></View>
            <View style={s.metaChip}><Ionicons name="briefcase-outline" size={12} color={T.textSec}/><Text style={s.metaChipT}>{jobType}</Text></View>
            {isRemote&&<View style={[s.metaChip,{backgroundColor:T.successBg}]}><Ionicons name="globe-outline" size={12} color={T.success}/><Text style={[s.metaChipT,{color:T.success}]}>Remote</Text></View>}
          </View>
        </View>
      </View>

      {/* Stats Row */}
      <View style={s.statsRow}>
        <View style={s.statBox}>
          <Text style={[s.statVal,{color:T.success}]}>{salary}</Text>
          <Text style={s.statLbl}>Salary</Text>
        </View>
        <View style={[s.statDivider]}/>
        <View style={s.statBox}>
          <Text style={[s.statVal,{color:T.primary}]}>{matchScore}%</Text>
          <Text style={s.statLbl}>Match</Text>
        </View>
        <View style={[s.statDivider]}/>
        <View style={s.statBox}>
          <Text style={[s.statVal,{color:deadline!==null&&deadline<=3?T.danger:T.warning}]}>{deadline!==null?`${deadline}d`:'—'}</Text>
          <Text style={s.statLbl}>Deadline</Text>
        </View>
        <View style={[s.statDivider]}/>
        <View style={s.statBox}>
          <Text style={[s.statVal,{color:'#8B5CF6'}]}>{job.applications_count||job.views_count||'—'}</Text>
          <Text style={s.statLbl}>{job.applications_count?'Applicants':'Views'}</Text>
        </View>
      </View>

      {/* Skill Tags */}
      {job.tags?.length>0 && (
        <View style={{flexDirection:'row',flexWrap:'wrap',gap:6,marginTop:12}}>
          {job.tags.slice(0,8).map((tag,i)=>{
            const tagStr = typeof tag==='string'?tag:String(tag);
            const isMatch = userSkills.some(us=>us.includes(tagStr.toLowerCase())||tagStr.toLowerCase().includes(us));
            return (
              <View key={i} style={[s.skillTag, isMatch&&{backgroundColor:T.successBg,borderColor:T.success}]}>
                {isMatch&&<Ionicons name="checkmark-circle" size={12} color={T.success} style={{marginRight:3}}/>}
                <Text style={[s.skillTagT, isMatch&&{color:T.success}]}>{tagStr}</Text>
              </View>
            );
          })}
        </View>
      )}

      {/* Badges */}
      <View style={{flexDirection:'row',gap:8,marginTop:12}}>
        {job.is_urgent&&<View style={[s.badge,{backgroundColor:T.dangerBg}]}><Ionicons name="flash" size={12} color={T.danger}/><Text style={[s.badgeT,{color:T.danger}]}>Urgent</Text></View>}
        {job.featured&&<View style={[s.badge,{backgroundColor:T.warningBg}]}><Ionicons name="star" size={12} color={T.warning}/><Text style={[s.badgeT,{color:T.warning}]}>Featured</Text></View>}
        {hasApplied&&<View style={[s.badge,{backgroundColor:T.successBg}]}><Ionicons name="checkmark-circle" size={12} color={T.success}/><Text style={[s.badgeT,{color:T.success}]}>Applied</Text></View>}
        {isSaved&&<View style={[s.badge,{backgroundColor:T.primaryBg}]}><Ionicons name="bookmark" size={12} color={T.primary}/><Text style={[s.badgeT,{color:T.primary}]}>Saved</Text></View>}
      </View>
    </Animated.View>
  );

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER: TABS
  // ═════════════════════════════════════════════════════════════════════════════
  const renderTabs = () => (
    <View style={s.tabBar}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{paddingHorizontal:16,gap:6}}>
        {TABS.map(tab=>(
          <TouchableOpacity key={tab.key} style={[s.tab,activeTab===tab.key&&s.tabActive]} onPress={()=>setActiveTab(tab.key)}>
            <Ionicons name={tab.icon} size={14} color={activeTab===tab.key?T.primary:T.textMut}/>
            <Text style={[s.tabT,activeTab===tab.key&&s.tabTActive]}>{tab.label}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER: LIST ITEM
  // ═════════════════════════════════════════════════════════════════════════════
  const ListItem = ({text,icon='checkmark-circle',color=T.success}) => (
    <View style={s.listItem}>
      <Ionicons name={icon} size={16} color={color} style={{marginTop:2}}/>
      <Text style={s.listItemT}>{typeof text==='string'?text:JSON.stringify(text)}</Text>
    </View>
  );

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER: TAB CONTENT
  // ═════════════════════════════════════════════════════════════════════════════
  const renderContent = () => {
    switch(activeTab) {
      case 'overview': return (
        <View style={s.contentSection}>
          <Text style={s.secTitle}>Job Description</Text>
          <Text style={s.descText}>{job.description||'No description available.'}</Text>
          {job.responsibilities?.length>0&&(<>
            <Text style={s.secTitle}>Key Responsibilities</Text>
            {job.responsibilities.map((r,i)=><ListItem key={i} text={r}/>)}
          </>)}
          {/* Extra Details */}
          {(job.workingHours||job.teamSize||job.reportingTo||job.travelRequired||job.visaSponsorship)&&(
            <>
              <Text style={s.secTitle}>Additional Details</Text>
              <View style={s.detailGrid}>
                {job.workingHours&&<View style={s.detailItem}><Ionicons name="time-outline" size={16} color={T.primary}/><View><Text style={s.detailLabel}>Working Hours</Text><Text style={s.detailVal}>{job.workingHours}</Text></View></View>}
                {job.teamSize&&<View style={s.detailItem}><Ionicons name="people-outline" size={16} color={T.primary}/><View><Text style={s.detailLabel}>Team Size</Text><Text style={s.detailVal}>{job.teamSize}</Text></View></View>}
                {job.reportingTo&&<View style={s.detailItem}><Ionicons name="person-outline" size={16} color={T.primary}/><View><Text style={s.detailLabel}>Reports To</Text><Text style={s.detailVal}>{job.reportingTo}</Text></View></View>}
                {job.travelRequired&&<View style={s.detailItem}><Ionicons name="airplane-outline" size={16} color={T.primary}/><View><Text style={s.detailLabel}>Travel</Text><Text style={s.detailVal}>{job.travelRequired}</Text></View></View>}
                {job.visaSponsorship&&<View style={s.detailItem}><Ionicons name="card-outline" size={16} color={T.primary}/><View><Text style={s.detailLabel}>Visa</Text><Text style={s.detailVal}>{job.visaSponsorship}</Text></View></View>}
                {job.securityClearance&&<View style={s.detailItem}><Ionicons name="shield-checkmark-outline" size={16} color={T.primary}/><View><Text style={s.detailLabel}>Clearance</Text><Text style={s.detailVal}>{job.securityClearance}</Text></View></View>}
              </View>
            </>
          )}
        </View>
      );

      case 'requirements': return (
        <View style={s.contentSection}>
          <Text style={s.secTitle}>Requirements</Text>
          {job.requirements?.length>0
            ? job.requirements.map((r,i)=><ListItem key={i} text={r} icon="checkmark-circle" color={T.primary}/>)
            : <Text style={s.emptyText}>No specific requirements listed.</Text>
          }
          {job.experience_min!=null&&(
            <View style={[s.detailItem,{marginTop:14}]}>
              <Ionicons name="briefcase-outline" size={16} color={T.primary}/>
              <View><Text style={s.detailLabel}>Experience Required</Text><Text style={s.detailVal}>{job.experience_min}–{job.experience_max||'10+'} years</Text></View>
            </View>
          )}
          {job.education_level&&(
            <View style={[s.detailItem,{marginTop:8}]}>
              <Ionicons name="school-outline" size={16} color={T.primary}/>
              <View><Text style={s.detailLabel}>Education</Text><Text style={s.detailVal}>{job.education_level}</Text></View>
            </View>
          )}
        </View>
      );

      case 'benefits': return (
        <View style={s.contentSection}>
          <Text style={s.secTitle}>Benefits & Perks</Text>
          {job.benefits?.length>0
            ? job.benefits.map((b,i)=><ListItem key={i} text={b} icon="gift" color={T.warning}/>)
            : <Text style={s.emptyText}>Benefits not listed.</Text>
          }
        </View>
      );

      case 'company': return (
        <View style={s.contentSection}>
          <Text style={s.secTitle}>About {company}</Text>
          <Text style={s.descText}>{job.companyInfo?.description||job.company_description||`${company} is a great place to work.`}</Text>
          <View style={s.companyStats}>
            {[
              {icon:'people',label:'Size',val:job.companyInfo?.size||job.company_size||'—'},
              {icon:'business',label:'Industry',val:job.companyInfo?.industry||job.industry||'—'},
              {icon:'calendar',label:'Founded',val:job.companyInfo?.founded||'—'},
            ].map((c,i)=>(
              <View key={i} style={s.companyStat}>
                <Ionicons name={c.icon} size={18} color={T.primary}/>
                <Text style={s.companyStatL}>{c.label}</Text>
                <Text style={s.companyStatV}>{c.val}</Text>
              </View>
            ))}
          </View>
          {(job.companyInfo?.website)&&(
            <TouchableOpacity style={s.websiteBtn} onPress={()=>Linking.openURL(job.companyInfo.website).catch(()=>{})}>
              <Ionicons name="globe-outline" size={18} color={T.primary}/>
              <Text style={{fontSize:14,fontWeight:'600',color:T.primary,marginHorizontal:8}}>Visit Website</Text>
              <Ionicons name="open-outline" size={14} color={T.primary}/>
            </TouchableOpacity>
          )}
          {/* Recruiter */}
          {job.recruiterInfo&&(
            <>
              <Text style={[s.secTitle,{marginTop:20}]}>Contact Recruiter</Text>
              <View style={s.recruiterCard}>
                <View style={{flex:1}}>
                  <Text style={{fontSize:15,fontWeight:'700',color:T.text}}>{job.recruiterInfo.name}</Text>
                  <Text style={{fontSize:13,color:T.textSec,marginTop:2}}>{job.recruiterInfo.title}</Text>
                </View>
                <View style={{flexDirection:'row',gap:8}}>
                  {job.recruiterInfo.email&&<TouchableOpacity style={s.contactBtn} onPress={()=>Linking.openURL(`mailto:${job.recruiterInfo.email}`)}><Ionicons name="mail" size={18} color={T.primary}/></TouchableOpacity>}
                  {job.recruiterInfo.phone&&<TouchableOpacity style={s.contactBtn} onPress={()=>Linking.openURL(`tel:${job.recruiterInfo.phone}`)}><Ionicons name="call" size={18} color={T.primary}/></TouchableOpacity>}
                  {job.recruiterInfo.linkedin&&<TouchableOpacity style={s.contactBtn} onPress={()=>Linking.openURL(job.recruiterInfo.linkedin)}><Ionicons name="logo-linkedin" size={18} color={T.primary}/></TouchableOpacity>}
                </View>
              </View>
            </>
          )}
        </View>
      );

      case 'similar': return (
        <View style={s.contentSection}>
          <Text style={s.secTitle}>Similar Jobs</Text>
          {similarJobs.length>0 ? similarJobs.map((sj,i)=>(
            <TouchableOpacity key={sj.id||i} style={s.similarCard} onPress={()=>navigation.push('JobDetailsScreen',{jobId:sj.id,job:sj})} activeOpacity={0.85}>
              <View style={{flex:1}}>
                <Text style={{fontSize:15,fontWeight:'700',color:T.text}} numberOfLines={1}>{sj.title}</Text>
                <Text style={{fontSize:13,color:T.primary,fontWeight:'600',marginTop:2}}>{sj.company||sj.department}</Text>
                <Text style={{fontSize:12,color:T.textSec,marginTop:2}}>{sj.location}</Text>
                <Text style={{fontSize:13,fontWeight:'700',color:T.success,marginTop:4}}>{sj.salary||fmtSalary(sj.salary_min,sj.salary_max)||'Competitive'}</Text>
              </View>
              <Ionicons name="chevron-forward" size={20} color={T.textMut}/>
            </TouchableOpacity>
          )) : <Text style={s.emptyText}>No similar jobs found.</Text>}
        </View>
      );

      default: return null;
    }
  };

  // ═════════════════════════════════════════════════════════════════════════════
  // RENDER: ACTION BAR
  // ═════════════════════════════════════════════════════════════════════════════
  const renderActions = () => (
    <View style={s.actionBar}>
      <TouchableOpacity
        style={[s.applyBtn, hasApplied&&{backgroundColor:T.success}, isApplying&&{opacity:0.6}]}
        onPress={handleApply}
        disabled={isApplying||hasApplied}
      >
        {isApplying ? <ActivityIndicator size={18} color={T.white}/> :
          <Ionicons name={hasApplied?'checkmark-circle':'paper-plane'} size={18} color={T.white}/>}
        <Text style={s.applyBtnT}>{hasApplied?'Applied':isApplying?'Applying...':'Apply Now'}</Text>
      </TouchableOpacity>
      <TouchableOpacity style={[s.saveBtn, isSaved&&{backgroundColor:T.primary,borderColor:T.primary}]} onPress={handleSave}>
        <Ionicons name={isSaved?'bookmark':'bookmark-outline'} size={20} color={isSaved?T.white:T.primary}/>
      </TouchableOpacity>
    </View>
  );

  // ═════════════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ═════════════════════════════════════════════════════════════════════════════
  return (
    <SafeAreaView style={{flex:1,backgroundColor:T.primary}}>
      <StatusBar barStyle="light-content" backgroundColor={T.primary}/>
      {renderHeader()}
      <ScrollView style={{flex:1,backgroundColor:T.bg}} showsVerticalScrollIndicator={false}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh} colors={[T.primary]} tintColor={T.primary}/>}>
        {renderHero()}
        {renderTabs()}
        <Animated.View style={{opacity:fadeAnim}}>
          {renderContent()}
        </Animated.View>
        <View style={{height:20}}/>
      </ScrollView>
      {renderActions()}
    </SafeAreaView>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// STYLES
// ═══════════════════════════════════════════════════════════════════════════════
const s = StyleSheet.create({
  header:     {flexDirection:'row',alignItems:'center',paddingHorizontal:16,paddingVertical:14},
  hdrBtn:     {width:38,height:38,borderRadius:12,backgroundColor:'rgba(255,255,255,0.18)',justifyContent:'center',alignItems:'center'},
  hdrTitle:   {fontSize:18,fontWeight:'700',color:'#FFF'},
  hdrSub:     {fontSize:12,color:'rgba(255,255,255,0.75)',marginTop:1},
  retryBtn:   {flexDirection:'row',alignItems:'center',backgroundColor:T.primary,paddingHorizontal:20,paddingVertical:12,borderRadius:12,marginTop:20},

  heroCard:   {backgroundColor:T.card,padding:18,borderBottomWidth:1,borderBottomColor:T.border},
  logoCircle: {width:56,height:56,borderRadius:16,backgroundColor:T.primaryBg,justifyContent:'center',alignItems:'center'},
  logoText:   {fontSize:24,fontWeight:'800',color:T.primary},
  jobTitle:   {fontSize:20,fontWeight:'800',color:T.text,lineHeight:26},
  companyName:{fontSize:15,fontWeight:'600',color:T.primary,marginTop:2},
  metaChip:   {flexDirection:'row',alignItems:'center',gap:3,backgroundColor:T.borderLight,paddingHorizontal:8,paddingVertical:4,borderRadius:8},
  metaChipT:  {fontSize:11,fontWeight:'500',color:T.textSec},
  statsRow:   {flexDirection:'row',backgroundColor:T.borderLight,borderRadius:14,padding:14,marginTop:4},
  statBox:    {flex:1,alignItems:'center'},
  statVal:    {fontSize:14,fontWeight:'800'},
  statLbl:    {fontSize:10,color:T.textMut,marginTop:2,fontWeight:'500'},
  statDivider:{width:1,backgroundColor:T.border,marginVertical:4},
  skillTag:   {flexDirection:'row',alignItems:'center',paddingHorizontal:10,paddingVertical:5,borderRadius:8,borderWidth:1,borderColor:T.border,backgroundColor:T.borderLight},
  skillTagT:  {fontSize:11,fontWeight:'600',color:T.textSec},
  badge:      {flexDirection:'row',alignItems:'center',gap:4,paddingHorizontal:8,paddingVertical:4,borderRadius:8},
  badgeT:     {fontSize:11,fontWeight:'700'},

  tabBar:     {backgroundColor:T.card,borderBottomWidth:1,borderBottomColor:T.border,paddingVertical:8},
  tab:        {flexDirection:'row',alignItems:'center',paddingHorizontal:14,paddingVertical:10,borderRadius:20,gap:4},
  tabActive:  {backgroundColor:T.primaryBg,borderWidth:1,borderColor:'#BFDBFE'},
  tabT:       {fontSize:13,fontWeight:'500',color:T.textMut},
  tabTActive: {color:T.primary,fontWeight:'700'},

  contentSection:{padding:20,backgroundColor:T.card,marginBottom:1},
  secTitle:   {fontSize:17,fontWeight:'700',color:T.text,marginBottom:12},
  descText:   {fontSize:14,color:'#475569',lineHeight:22,marginBottom:16},
  emptyText:  {fontSize:14,color:T.textMut,fontStyle:'italic',textAlign:'center',paddingVertical:20},
  listItem:   {flexDirection:'row',alignItems:'flex-start',marginBottom:10,gap:10},
  listItemT:  {fontSize:14,color:'#475569',lineHeight:20,flex:1},

  detailGrid: {gap:10},
  detailItem: {flexDirection:'row',alignItems:'center',gap:10,backgroundColor:T.borderLight,borderRadius:10,padding:12},
  detailLabel:{fontSize:11,color:T.textMut,fontWeight:'500'},
  detailVal:  {fontSize:13,fontWeight:'600',color:T.text,marginTop:1},

  companyStats:{flexDirection:'row',gap:8,marginVertical:14},
  companyStat:{flex:1,alignItems:'center',backgroundColor:T.borderLight,borderRadius:14,padding:14},
  companyStatL:{fontSize:11,color:T.textMut,marginTop:6,fontWeight:'500'},
  companyStatV:{fontSize:13,fontWeight:'700',color:T.text,marginTop:2,textAlign:'center'},
  websiteBtn: {flexDirection:'row',alignItems:'center',justifyContent:'center',backgroundColor:T.primaryBg,borderWidth:1,borderColor:'#BFDBFE',borderRadius:12,paddingVertical:12,marginTop:10},
  recruiterCard:{flexDirection:'row',alignItems:'center',backgroundColor:T.borderLight,borderRadius:14,padding:14,borderWidth:1,borderColor:T.border},
  contactBtn: {width:40,height:40,borderRadius:10,backgroundColor:T.card,justifyContent:'center',alignItems:'center',borderWidth:1,borderColor:T.border},
  similarCard:{flexDirection:'row',alignItems:'center',backgroundColor:T.borderLight,borderRadius:14,padding:14,marginBottom:10,borderWidth:1,borderColor:T.border},

  actionBar:  {flexDirection:'row',paddingHorizontal:16,paddingVertical:12,backgroundColor:T.card,borderTopWidth:1,borderTopColor:T.border,gap:10},
  applyBtn:   {flex:1,flexDirection:'row',alignItems:'center',justifyContent:'center',backgroundColor:T.primary,borderRadius:14,paddingVertical:14,gap:8},
  applyBtnT:  {fontSize:15,fontWeight:'700',color:T.white},
  saveBtn:    {width:52,height:52,borderRadius:14,justifyContent:'center',alignItems:'center',borderWidth:2,borderColor:T.primary,backgroundColor:T.card},
});
